// /apostille/$country — per-destination Apostille / Legalization landing
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import type { ApostilleDeepEntryTh } from "@/data/apostille-deep-th";
import CountryDeepContent from "@/components/CountryDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { APOSTILLE_COUNTRIES, getApostilleCountry } from "@/data/apostille-countries";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import ApostilleTripleKeyLinks from "@/components/ApostilleTripleKeyLinks";
import ApostilleReverseLinks from "@/components/ApostilleReverseLinks";
import { Stamp, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, ListChecks, AlertTriangle, BookOpen, ExternalLink } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/apostille/$country/")({
  loader: async ({ params }) => {
    const { APOSTILLE_DEEP_TH } = await import("@/data/apostille-deep-th");
    const deep: ApostilleDeepEntryTh | null = APOSTILLE_DEEP_TH[params.country] ?? null;
    if (!getApostilleCountry(params.country)) throw notFound();
    return { deep, slug: params.country };
  },
  head: ({ params, loaderData }) => {
    const c = getApostilleCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("ไม่พบประเทศ", "/apostille/$country", "th")}] };
    const deep = loaderData?.deep;
    const url = `${SITE}/apostille/${c.slug}`;
    const label = c.isHague ? "Apostille" : "Consular Legalization";
    const title = deep?.metaTitle ?? `${label} เอกสารไทย → ${c.nameTh} ${c.flag} เริ่ม ฿${c.priceFromTh.toLocaleString()} | NYC`;
    const desc = deep?.metaDescription ?? `บริการ ${label} เอกสารไทยเพื่อใช้ที่${c.nameTh} (${c.nameEn}) ครบ: แปล + MFA + ${c.authority} เสร็จ ${c.turnaround} — ปรึกษาฟรี ทักไลน์รับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(title, "/apostille/$country", "th") }, { name: "description", content: ctaDesc(desc, "/apostille/$country", "th")},
        { property: "og:title", content: ctaTitle(title, "/apostille/$country", "th")}, { property: "og:description", content: ctaDesc(desc, "/apostille/$country", "th")},
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/apostille/${c.slug}`, `/en/apostille/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${label} เอกสารไทย → ${c.nameEn}`,
              serviceType: c.isHague ? "Apostille Service" : "Consular Legalization Service",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price: c.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธี ${label} เอกสารไทย เพื่อใช้ที่${c.nameTh}`,
              totalTime: "P7D",
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งเอกสาร", text: "ส่งภาพหรือสำเนาผ่าน LINE @nycli — ทีมประเมินภายใน 30 นาที" },
                { "@type": "HowToStep", position: 2, name: "แปลโดยนักแปลขึ้นทะเบียน", text: "แปล TH↔EN/ภาษาปลายทาง โดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม" },
                { "@type": "HowToStep", position: 3, name: "MFA Authentication", text: "ยื่นกรมการกงสุล กระทรวงต่างประเทศ — Apostille (2024-ปัจจุบัน) หรือรับรองธรรมดา" },
                ...(c.isHague ? [] : [{ "@type": "HowToStep", position: 4, name: "Consular Legalization", text: `ยื่นสถานทูต${c.nameTh}ในไทย เพื่อตรา Legalization ขั้นสุดท้าย` }]),
                { "@type": "HowToStep", position: c.isHague ? 4 : 5, name: "ส่งคืน", text: "ส่ง EMS / Grab ทั่ว กทม. หรือรับเอง" },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Apostille / Legalization", item: `${SITE}/apostille` },
                { "@type": "ListItem", position: 3, name: `${label} → ${c.nameTh}`, item: url },
              ],
            }),
          },
          ...(deep ? [{
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              inLanguage: "th",
              mainEntity: deep.faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }] : []),
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: ApostillePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบประเทศ</h1>
      <Link to="/apostille" className="mt-4 inline-block text-gold underline">กลับ Apostille hub</Link>
          <QuotableStatsStrip cluster="apostille" locale="th" variant="light" />
    </main>
  ),
});

function ApostillePage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getApostilleCountry(slug)!;
  const deep = (Route.useLoaderData() as { deep: ApostilleDeepEntryTh | null }).deep;
  const others = APOSTILLE_COUNTRIES.filter((x) => x.slug !== slug && x.region === c.region).slice(0, 6);
  const label = c.isHague ? "Apostille" : "Consular Legalization";

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/apostille" className="hover:text-gold">Apostille</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {label} · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            {label} เอกสารไทย → {c.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{c.notes}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "วิธีรับรอง", v: label },
              { k: "ราคาเริ่ม", v: `฿${c.priceFromTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: c.turnaround },
              { k: "หน่วยงานปลายทาง", v: c.authority },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          {deep?.speakable ?? `NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) ให้บริการ ${label} เอกสารไทยเพื่อใช้ที่${c.nameTh} (${c.nameEn}) — รวมแปล + รับรองนักแปล + MFA + ${c.isHague ? "ตรา Apostille" : `Consular Legalization สถานทูต${c.nameTh}`} เริ่ม ฿${c.priceFromTh.toLocaleString()} เสร็จ ${c.turnaround} บริการสำเร็จ 2,147+ เคส`}
        </blockquote>

        {deep && (
          <>
            <section className="mt-10">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เงื่อนไขของหน่วยงาน{c.nameTh}</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{deep.overview}</p>
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-[14px] text-slate-800">
                <strong className="text-amber-800">อายุเอกสารที่ยอมรับ:</strong> {deep.acceptanceWindow}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <ListChecks className="h-6 w-6 text-amber-600" />
                ขั้นตอน {label} ครบทุกขั้น ({c.nameTh})
              </h2>
              <ol className="mt-5 space-y-3">
                {deep.pipeline.map((s, i) => (
                  <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="font-bold text-navy-deep">{s.step}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{s.detail}</div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ตารางเอกสาร — ใช้กับอะไร ทำไม</h2>
              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-slate-50 text-navy-deep">
                    <tr>
                      <th className="px-4 py-3">เอกสาร</th>
                      <th className="px-4 py-3">ใช้สำหรับ</th>
                      <th className="px-4 py-3">หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {deep.docMatrix.map((v, i) => (
                      <tr key={i} className="bg-white">
                        <td className="px-4 py-3 font-semibold text-slate-800">{v.doc}</td>
                        <td className="px-4 py-3 text-slate-700">{v.need}</td>
                        <td className="px-4 py-3 text-slate-600">{v.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
                ข้อผิดพลาดที่พบบ่อยและเราป้องกันให้
              </h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {deep.pitfalls.map((p, i) => (
                  <div key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                    <div className="font-bold text-rose-900">{p.title}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{p.detail}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย — Apostille {c.nameTh}</h2>
              <div className="mt-5 space-y-3">
                {deep.faqs.map((f, i) => (
                  <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-4 open:border-amber-300 open:bg-amber-50/30">
                    <summary className="cursor-pointer font-bold text-navy-deep group-open:text-amber-700">{f.q}</summary>
                    <div className="mt-2 text-[14px] leading-relaxed text-slate-700">{f.a}</div>
                  </details>
                ))}
              </div>
            </section>

            {deep.citations.length > 0 && (
              <section className="mt-12 border-t border-slate-200 pt-10">
                <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                  <BookOpen className="h-6 w-6 text-amber-600" />
                  แหล่งอ้างอิงทางการ
                </h2>
                <ul className="mt-5 space-y-2 text-[14px]">
                  {deep.citations.map((cit, i) => (
                    <li key={i}>
                      <a href={cit.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-amber-700 underline-offset-2 hover:underline">
                        {cit.label}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}



        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่นิยม {label} ใช้ที่{c.nameTh}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {c.commonUses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอน {label}</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: 1, t: "ส่งเอกสาร", d: "LINE / Email" },
              { n: 2, t: "แปล + รับรองนักแปล", d: "นักแปลขึ้นทะเบียน" },
              { n: 3, t: c.isHague ? "MFA Apostille" : "MFA + สถานทูต", d: c.isHague ? "กรมการกงสุล" : `สถานทูต${c.nameTh}` },
              { n: 4, t: "ส่งคืน", d: "EMS / Grab" },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {label} เอกสารไทย → {c.nameTh} เริ่ม ฿{c.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมแปลโดยนักแปลขึ้นทะเบียน</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 2 วันทำการ +฿2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ค่าธรรมเนียม MFA / สถานทูต แยกตามจริง</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ส่ง EMS / Grab ทั่วโลก ตามจริง</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เลือกประเภทเอกสารที่ต้องการ {label}</h2>
          <p className="mt-2 text-sm text-slate-600">ดูราคา · ขั้นตอน · คำถามพบบ่อย เฉพาะสำหรับ {c.nameTh}</p>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {TRANSLATION_DOCTYPES.map((d) => (
              <Link key={d.slug} to="/apostille/$country/$doctype" params={{ country: c.slug, doctype: d.slug }}
                className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="flex-1 min-w-0">
                  <div className="truncate font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{d.nameTh}</div>
                  <div className="mt-0.5 truncate text-[11px] text-slate-500">เริ่ม ฿{(c.priceFromTh + d.surcharge).toLocaleString()} · {d.nameEn}</div>
                </div>
                <FileCheck2 className="ml-2 h-4 w-4 shrink-0 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/apostille/$country" params={{ country: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.isHague ? "Apostille" : "Legalization"} · เริ่ม ฿{o.priceFromTh.toLocaleString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <ApostilleReverseLinks mode="th" apostilleSlug={c.slug} countryNameTh={c.nameTh} countryNameEn={c.nameEn} isHague={c.isHague} />
        <ApostilleTripleKeyLinks mode="th" countryNameTh={c.nameTh} countryNameEn={c.nameEn} isHague={c.isHague} />
      </div>

      <CountryDeepContent lang="th" service="apostille" slug={c.slug} countryTh={c.nameTh} countryEn={c.nameEn} flag={c.flag} hague={c.isHague} authority={c.authority} priceFromTh={c.priceFromTh} turnaround={c.turnaround} />
      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="apostille" locale="th" variant="light" />
    </main>
  );
}
